import { useState } from 'react';
import { AlertCircle } from 'lucide-react';

import { productionApi } from 'api/production';
import ColorInput from 'components/storefront/StorefrontConfigEditor/components/ColorInput';
import EditableList, { ListItemDetail } from 'components/storefront/StorefrontConfigEditor/components/EditableList';
import ImageInput from 'components/storefront/StorefrontConfigEditor/components/ImageInput';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Textarea } from 'shadcn/components/ui/Textarea';
import CharacterCounter from 'shared/components/CharacterCounter';
import { CONTENT_LINK_URL_PATTERN, CONTENT_LINK_URL_TITLE } from 'shared/utils/contentLinkUrl';

import { DEFAULT_BANNER_ITEM, MIN_BANNER_ITEMS } from './constants';

/**
 * Form component for editing individual banner items in the carousel
 *
 * @param {Object} props
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {number} props.index - Index of the current banner item
 * @param {Object} props.listItem - Current banner item data
 * @param {Function} props.onChange - Callback function for handling item changes
 * @param {Function} props.onInvalidInput - Callback function for handling invalid input
 * @param {Object} props.slice - Parent data configuration object
 * @returns {JSX.Element} ListItemForm component
 */
function ListItemForm({ disabled, index, listItem, onChange, onInvalidInput, slice }) {
    const [imageError, setImageError] = useState(null);

    const handleChange = (e) => {
        onChange({ [e.target.name]: e.target.value });
    };

    const handleImageUpload = async (file) => {
        try {
            const response = await productionApi.uploadStorefrontConfigImage(file);
            const imageUrl = response.data.imagePath;
            onChange({ imageUrl });
            setImageError(null);
        } catch (e) {
            setImageError(`Error: ${e.message}`);
        }
    };

    const handleClearImage = () => {
        onChange({ imageUrl: '' });
    };

    return (
        <>
            <ImageInput
                id={`image-${slice.id}-${index}`}
                imageUrl={listItem.imageUrl}
                error={imageError}
                onClearImage={handleClearImage}
                onImageUpload={handleImageUpload}
                onInvalid={onInvalidInput}
                disabled={disabled}
            />

            <div className="flex flex-col gap-2">
                <Label htmlFor={`name-${slice.id}-${index}`}>Name</Label>
                <Input
                    type="text"
                    id={`name-${slice.id}-${index}`}
                    name="name"
                    value={listItem.name ?? ''}
                    placeholder="Enter name"
                    onChange={handleChange}
                    required
                    onInvalid={onInvalidInput}
                    disabled={disabled}
                    maxLength={25}
                />
                <CharacterCounter currentCount={listItem.name?.length ?? 0} maxLength={25} />
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`description-${slice.id}-${index}`}>Description</Label>
                <Textarea
                    id={`description-${slice.id}-${index}`}
                    name="description"
                    value={listItem.description ?? ''}
                    placeholder="Enter description"
                    onChange={handleChange}
                    rows="3"
                    disabled={disabled}
                    maxLength={61}
                />
                <CharacterCounter currentCount={listItem.description?.length ?? 0} maxLength={61} />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                    <Label htmlFor={`linkText-${slice.id}-${index}`}>Link Text</Label>
                    <Input
                        type="text"
                        id={`linkText-${slice.id}-${index}`}
                        name="linkText"
                        value={listItem.linkText ?? ''}
                        placeholder="Enter link text"
                        onChange={handleChange}
                        disabled={disabled}
                        maxLength={23}
                    />
                    <CharacterCounter currentCount={listItem.linkText?.length ?? 0} maxLength={23} />
                </div>

                <div className="flex flex-col gap-2">
                    <Label htmlFor={`linkUrl-${slice.id}-${index}`}>Link URL</Label>
                    <Input
                        type="text"
                        id={`linkUrl-${slice.id}-${index}`}
                        name="linkUrl"
                        value={listItem.linkUrl ?? ''}
                        placeholder="Enter link URL"
                        onChange={handleChange}
                        disabled={disabled}
                        pattern={CONTENT_LINK_URL_PATTERN}
                        title={CONTENT_LINK_URL_TITLE}
                    />
                </div>
            </div>

            <ColorInput
                id={`bgColor-${slice.id}-${index}`}
                label="Background Color"
                name="bgColor"
                value={listItem.bgColor}
                onChange={handleChange}
                disabled={disabled}
            />
        </>
    );
}

/**
 * Preview component for displaying banner item details
 *
 * @param {Object} props
 * @param {Object} props.listItem - Banner item data to preview
 * @returns {JSX.Element} ListItemPreview component
 */
function ListItemPreview({ listItem }) {
    return (
        <>
            <ListItemDetail label="Image" value={listItem} type="image" />
            <ListItemDetail label="Name" value={listItem.name} />
            <ListItemDetail label="Description" value={listItem.description} />
            <ListItemDetail label="Link Text" value={listItem.linkText} />
            <ListItemDetail label="Link URL" value={listItem.linkUrl} />
            <ListItemDetail label="Background Color" value={listItem.bgColor} type="bg-color" />
        </>
    );
}

/**
 * Generic Banner Carousel 1 form component.
 *
 * Renders the form fields for a banner carousel: a name input and an editable
 * list of banner items. This component is context-agnostic and can be used in
 * both the StorefrontConfigEditor and the AisleContent editor.
 *
 * @param {Object} props
 * @param {Object} props.data - Banner carousel data object (must include `id`, `name`, `items`)
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {Function} props.onChange - Callback function for handling data changes
 * @returns {JSX.Element} BannerCarousel1Form component
 */
export default function BannerCarousel1Form({ data, disabled, onChange }) {
    const handleItemsChange = (updatedListItems) => {
        onChange({ ...data, items: updatedListItems });
    };

    const handleNameChange = (e) => {
        onChange({ ...data, name: e.target.value });
    };

    const handleEyebrowChange = (e) => {
        onChange({ ...data, eyebrow: e.target.value });
    };

    const handleHeadingChange = (e) => {
        onChange({ ...data, heading: e.target.value });
    };

    return (
        <div className="flex flex-col gap-6">
            {data.items.length < MIN_BANNER_ITEMS && (
                <Alert variant="destructive">
                    <AlertCircle />
                    <AlertTitle>Warning</AlertTitle>
                    <AlertDescription>
                        A minimum of {MIN_BANNER_ITEMS} banner items are required for publication. Currently:{' '}
                        {data.items.length}/{MIN_BANNER_ITEMS}
                    </AlertDescription>
                </Alert>
            )}

            <div className="flex flex-col gap-1.5">
                <Label htmlFor={`eyebrow-${data.id}`}>Eyebrow</Label>
                <Input
                    type="text"
                    id={`eyebrow-${data.id}`}
                    name="eyebrow"
                    value={data.eyebrow ?? ''}
                    placeholder="Enter eyebrow"
                    onChange={handleEyebrowChange}
                    disabled={disabled}
                />
            </div>

            <div className="flex flex-col gap-1.5">
                <Label htmlFor={`heading-${data.id}`}>Heading</Label>
                <Input
                    type="text"
                    id={`heading-${data.id}`}
                    name="heading"
                    value={data.heading ?? ''}
                    placeholder="Enter heading"
                    onChange={handleHeadingChange}
                    disabled={disabled}
                />
            </div>

            <div className="flex flex-col gap-1.5">
                <Label htmlFor={`name-${data.id}`}>Name</Label>
                <p className="text-xs text-muted-foreground -mt-0.5">
                    For internal reference only. Not displayed to customers.
                </p>
                <Input
                    type="text"
                    id={`name-${data.id}`}
                    name="name"
                    value={data.name ?? ''}
                    placeholder="Enter carousel name"
                    onChange={handleNameChange}
                    disabled={disabled}
                />
            </div>

            <EditableList
                defaultItem={DEFAULT_BANNER_ITEM}
                disabled={disabled}
                ListItemForm={ListItemForm}
                ListItemPreview={ListItemPreview}
                listItems={data.items}
                onChange={handleItemsChange}
                slice={data}
            />
        </div>
    );
}
